import type {
  AssetsSearchMetricsBodyDto,
  AssetsSearchMetricsReturnDto,
} from '@terra/shared-contracts-assets';
import {
  ASSETS_VIEW,
  ASSET_TYPE,
  type AssetType,
  type AssetsSecurityFilter,
  type AssetsView,
} from '../../../store/tab/assetsTabScopedFilterStore';

export const ASSETS_SEGMENT = {
  ALL: 'all',
  WITH_FINDINGS: 'withFindings',
  WITH_SIGNALS: 'withSignals',
  NO_DETECTIONS: 'noDetections',
  WEB_ENDPOINTS: 'webEndpoints',
  AGENTS: 'agents',
} as const;

export type AssetsSegment =
  (typeof ASSETS_SEGMENT)[keyof typeof ASSETS_SEGMENT];

type AssetsSegmentFilters = {
  typeFilter: AssetType;
  securityFilter: AssetsSecurityFilter | null;
};

export const ASSETS_SEGMENT_FILTERS: Record<
  AssetsSegment,
  AssetsSegmentFilters
> = {
  [ASSETS_SEGMENT.ALL]: {
    typeFilter: ASSET_TYPE.ENDPOINTS,
    securityFilter: null,
  },
  [ASSETS_SEGMENT.WITH_FINDINGS]: {
    typeFilter: ASSET_TYPE.ENDPOINTS,
    securityFilter: 'vulnerable',
  },
  [ASSETS_SEGMENT.WITH_SIGNALS]: {
    typeFilter: ASSET_TYPE.ENDPOINTS,
    securityFilter: 'sensitive',
  },
  [ASSETS_SEGMENT.NO_DETECTIONS]: {
    typeFilter: ASSET_TYPE.ENDPOINTS,
    securityFilter: 'clear',
  },
  [ASSETS_SEGMENT.WEB_ENDPOINTS]: {
    typeFilter: ASSET_TYPE.WEB_ENDPOINTS,
    securityFilter: null,
  },
  [ASSETS_SEGMENT.AGENTS]: {
    typeFilter: ASSET_TYPE.AGENTS,
    securityFilter: null,
  },
};

export const ASSETS_SEGMENTS_BY_VIEW: Record<
  AssetsView,
  readonly [AssetsSegment, ...AssetsSegment[]]
> = {
  [ASSETS_VIEW.ENDPOINTS]: [
    ASSETS_SEGMENT.ALL,
    ASSETS_SEGMENT.WITH_FINDINGS,
    ASSETS_SEGMENT.WITH_SIGNALS,
    ASSETS_SEGMENT.NO_DETECTIONS,
    ASSETS_SEGMENT.WEB_ENDPOINTS,
    ASSETS_SEGMENT.AGENTS,
  ],
  [ASSETS_VIEW.WEB_PAGES]: [ASSETS_SEGMENT.ALL],
  [ASSETS_VIEW.UNVERIFIED]: [ASSETS_SEGMENT.ALL],
};

export type AssetsSegmentCounts = {
  endpointTypes: AssetsSearchMetricsReturnDto['endpointTypeCounts'] | null;
  webPageCount: number | null;
  unverifiedCount: number | null;
  security: Record<AssetsSecurityFilter, number> | null;
};

export const ASSETS_VIEW_METRICS_TYPE: Record<
  AssetsView,
  Extract<
    AssetsSearchMetricsBodyDto['type'],
    'allEndpoints' | 'webpages'
  > | null
> = {
  [ASSETS_VIEW.ENDPOINTS]: 'allEndpoints',
  [ASSETS_VIEW.WEB_PAGES]: 'webpages',
  [ASSETS_VIEW.UNVERIFIED]: null,
};

export const toAssetsSegmentCounts = ({
  view,
  metrics,
  unverifiedCount,
}: {
  view: AssetsView;
  metrics: AssetsSearchMetricsReturnDto | null;
  unverifiedCount: number | null;
}): AssetsSegmentCounts => {
  const isEndpointsView = view === ASSETS_VIEW.ENDPOINTS;

  return {
    endpointTypes: isEndpointsView
      ? (metrics?.endpointTypeCounts ?? null)
      : null,
    webPageCount:
      view === ASSETS_VIEW.WEB_PAGES ? (metrics?.total ?? null) : null,
    unverifiedCount,
    security: isEndpointsView ? (metrics?.securityCounts ?? null) : null,
  };
};

export const resolveSelectedAssetsSegment = ({
  view,
  typeFilter,
  securityFilter,
}: {
  view: AssetsView;
  typeFilter: AssetType;
  securityFilter: AssetsSecurityFilter | null;
}): AssetsSegment => {
  const [defaultSegment, ...otherSegments] = ASSETS_SEGMENTS_BY_VIEW[view];

  return (
    otherSegments.find((segment) => {
      const filters = ASSETS_SEGMENT_FILTERS[segment];

      return (
        filters.typeFilter === typeFilter &&
        filters.securityFilter === securityFilter
      );
    }) ?? defaultSegment
  );
};

const resolveAllSegmentCount = ({
  view,
  counts,
}: {
  view: AssetsView;
  counts: AssetsSegmentCounts;
}): number | undefined => {
  switch (view) {
    case ASSETS_VIEW.ENDPOINTS:
      return counts.endpointTypes
        ? counts.endpointTypes.endpoints + counts.endpointTypes.agents
        : undefined;
    case ASSETS_VIEW.WEB_PAGES:
      return counts.webPageCount ?? undefined;
    case ASSETS_VIEW.UNVERIFIED:
      return counts.unverifiedCount ?? undefined;
  }
};

export const resolveAssetsSegmentCount = ({
  segment,
  view,
  counts,
}: {
  segment: AssetsSegment;
  view: AssetsView;
  counts: AssetsSegmentCounts;
}): number | undefined => {
  if (segment === ASSETS_SEGMENT.ALL) {
    return resolveAllSegmentCount({ view, counts });
  }

  if (segment === ASSETS_SEGMENT.WEB_ENDPOINTS) {
    return counts.endpointTypes?.endpoints;
  }

  if (segment === ASSETS_SEGMENT.AGENTS) {
    return counts.endpointTypes?.agents;
  }

  const { securityFilter } = ASSETS_SEGMENT_FILTERS[segment];

  return securityFilter && counts.security
    ? counts.security[securityFilter]
    : undefined;
};
